<template>
    <div class="ajustes-detail-container">
        <!-- Estado de carga -->
        <div v-if="isLoading" class="text-center py-5">
            <b-spinner
                variant="primary"
                style="width: 3rem; height: 3rem;"
            ></b-spinner>
            <p class="mt-2 text-muted">
                {{ internalLabels.loading || 'Cargando...' }}
            </p>
        </div>

        <!-- Tabla de ajustes origen con CorporateDataTable -->
        <div
            v-if="!isLoading && ajustOrigen && ajustOrigen.length > 0"
            class="ajustes-detail-table-container"
        >
            <CorporateDataTable
                :items="ajustOrigen"
                :fields="tableFields"
                :labels="tableLabels"
                :default-per-page="defaultPerPage"
                :page-options="pageOptions"
                :narrow="false"
                :table-min-height="490"
                :table-max-height="490"
                :show-search="true"
                :allow-column-toggle="true"
                :enable-sorting="true"
                :filter-defs="tableFilterDefs"
                :toolbar-disabled="false"
                :show-back-button="showBackButton"
                :back-button-text="internalLabels.volver || 'Volver'"
                @back="goBack"
                class="ajustes-detail-table"
            >
                <!-- Toolbar con botones (sin título) -->
                <template #toolbar-actions>
                    <div class="detail-toolbar-left d-flex align-items-center">
                        <!-- ESPACIO VACÍO - SIN TÍTULO -->
                    </div>

                    <div class="detail-toolbar-right d-flex align-items-center">
                        <b-button
                            size="sm"
                            class="table-toolbar-add-btn table-toolbar-add-btn-origin"
                            @click="adicionaCuentasOrigen()"
                            :disabled="readOnly"
                        >

                            {{
                                internalLabels.adicionarCuenta ||
                                'Adición Cuenta'
                            }}
                        </b-button>

                        <b-button
                            size="sm"
                            class="table-toolbar-add-btn table-toolbar-add-btn-origin-new ml-2"
                            @click="adicionaCuentaNueva()"
                            :disabled="readOnly"
                        >

                            {{
                                internalLabels.adicionarCuentaNueva ||
                                'Adición Cuenta Nueva'
                            }}
                        </b-button>
                    </div>
                </template>

                <!-- Estado vacío personalizado -->
                <template #empty-state>
                    <div class="text-center py-5">
                        <font-awesome-icon
                            :icon="['fas', 'file-alt']"
                            size="4x"
                            class="text-muted mb-3"
                            style="opacity: 0.3;"
                        />

                        <p class="h5 text-muted">
                            {{
                                internalLabels.noData ||
                                'No hay cuentas de origen registradas'
                            }}
                        </p>

                        <div class="mt-3">
                            <b-button
                                variant="outline-primary"
                                size="sm"
                                @click="adicionaCuentasOrigen()"
                                :disabled="readOnly"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'plus-circle']"
                                    class="mr-1"
                                />

                                {{
                                    internalLabels.adicionarCuenta ||
                                    'Adición Cuenta'
                                }}
                            </b-button>

                            <b-button
                                variant="primary"
                                size="sm"
                                class="ml-2"
                                @click="adicionaCuentaNueva()"
                                :disabled="readOnly"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'plus-square']"
                                    class="mr-1"
                                />

                                {{
                                    internalLabels.adicionarCuentaNueva ||
                                    'Adición Cuenta Nueva'
                                }}
                            </b-button>
                        </div>
                    </div>
                </template>

                <!-- Columna Cuenta -->
                <template #cell(cuenta)="data">
                    <div class="text-left font-weight-bold">
                        {{ data.value }}
                    </div>
                </template>

                <!-- Columna Descripción -->
                <template #cell(descripcion)="data">
                    <div class="text-left">
                        {{ data.value }}
                    </div>
                </template>

                <!-- Columna Valor -->
                <template #cell(valor)="data">
                    <div class="text-right">
                        {{ formatCurrency(data.value) }}
                    </div>
                </template>

                <!-- Columna Funcional -->
                <template #cell(funcional)="data">
                    <div class="text-right">
                        {{ data.value || '-' }}
                    </div>
                </template>

                <!-- Columna Alterna -->
                <template #cell(alterna)="data">
                    <div class="text-right">
                        {{ data.value || '-' }}
                    </div>
                </template>

                <!-- Columna Editar -->
                <template #cell(accionEditar)="data">
                    <b-button
                        style="background-color: #FF924C; width: 30px; height: 30px; border: none; border-radius: 4px;"
                        class="md-fab md-primary table-action-btn"
                        @click="editAjuste(data.item.id)"
                        :disabled="readOnly"
                    >
                        <i>
                            <font-awesome-icon
                                :icon="['fas', 'pencil-alt']"
                                size="xs"
                            />
                        </i>
                    </b-button>
                </template>

                <!-- Columna Eliminar -->
                <template #cell(accionEliminar)="data">
                    <b-button
                        class="reset-btn-danger table-action-btn"
                        style="width: 30px; height: 30px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border: none; border-radius: 4px;"
                        @click="borraCuentasOrigen(data.item.id)"
                        :disabled="readOnly"
                    >
                        <i>
                            <font-awesome-icon
                                :icon="['fas', 'trash']"
                                size="xs"
                            />
                        </i>
                    </b-button>
                </template>
            </CorporateDataTable>
        </div>

        <!-- Mensaje cuando no hay datos -->
        <div
            v-if="
                !isLoading &&
                (!ajustOrigen || ajustOrigen.length === 0)
            "
            class="admin-table-block"
        >
            <h5>
                {{
                    internalLabels.noData ||
                    'No hay cuentas de origen registradas'
                }}
            </h5>
        </div>

        <!-- Modales -->
        <EditAjustesOrigenVue
            :showEdiAjustOrig="showEdiAjustOrig"
            :dataEdit="ajustes"
            :listCuentas="listCuentas"
            :listCuentasSelMul="listCuentasSelMul"
            :idCompania="id_compania"
            :codReporte="codReporte"
            :vigencia="vigencia"
            :secuencia="
                ajustOrigen && ajustOrigen.length > 0
                    ? ajustOrigen[0].secuencia
                    : null
            "
            :tipo="
                ajustOrigen && ajustOrigen.length > 0
                    ? ajustOrigen[0].tipo
                    : null
            "
            :tipo_ajuste="
                ajustOrigen && ajustOrigen.length > 0
                    ? ajustOrigen[0].tipo_ajuste
                    : null
            "
            @reloadTblOrig="recargaTabla"
            @closeModal="cierraModal"
        />

        <ModalCuentaNuevaVue
            :showModal="showModalCuentaNueva"
            :idCompania="id_compania"
            :codReporte="codReportePrincipal"
            :vigencia="vigencia"
            :secuencia="
                ajustOrigen && ajustOrigen.length > 0
                    ? ajustOrigen[0].secuencia
                    : null
            "
            :tipo_ajuste="
                ajustOrigen && ajustOrigen.length > 0
                    ? ajustOrigen[0].tipo_ajuste
                    : null
            "
            :tipo="
                ajustOrigen && ajustOrigen.length > 0
                    ? ajustOrigen[0].tipo
                    : null
            "
            @closeModal="cierraModalCuentaNueva"
            @cuentaCreada="cuentaCreada"
        />
    </div>
</template>

<script>
import Ajustes from '../../services/AjustesConsol'
import EditAjustesOrigenVue from './EditAjustesOrigen.vue'
import ModalCuentaNuevaVue from './CuentaNuevaOrigen.vue'
import AjustesConsol from '../../services/AjustesConsol'
import PlanCuentas from '../../services/PlanCuentas'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "AjustesOrigen",

    components: {
        EditAjustesOrigenVue,
        ModalCuentaNuevaVue,
        CorporateDataTable
    },

    props: {
        ajustOrigen: {
            type: Array,
            default: () => []
        },

        codReporte: {
            type: [String, Number],
            default: null
        },

        id_compania: {
            type: [String, Number],
            default: null
        },

        vigencia: {
            type: [String, Number],
            default: null
        },

        nombreCompania: {
            type: String,
            default: ''
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        // Props para la tabla
        tableLabels: {
            type: Object,
            default: () => ({})
        },

        tableFilterDefs: {
            type: Array,
            default: () => []
        },

        defaultPerPage: {
            type: Number,
            default: 10
        },

        pageOptions: {
            type: Array,
            default: () => [10, 20, 50, 100]
        },

        // Prop para mostrar el botón volver
        showBackButton: {
            type: Boolean,
            default: false
        },

        // Prop para ocultar el título (si se pasa desde el padre)
        showCompanyName: {
            type: Boolean,
            default: true
        }
    },

    data: () => ({
        ajustes: [],
        showEdiAjustOrig: false,
        showModalCuentaNueva: false,
        listCuentas: [],
        listCuentasSelMul: [],
        selectedRow: null,
        isLoading: false,
        codReportePrincipal: null,
    }),

    async mounted() {
        this.codReportePrincipal = await this.getReportePrincipal();
    },

    computed: {
        internalLabels() {
            const defaultLabels = {
                titleTbl: "Ajustes Origen",
                cuenta: "Cuenta",
                descripcion: "Descripción",
                valor: "Valor",
                funcional: "Funcional",
                alterna: "Alterna",
                eliminar: "Eliminar",
                editar: "Editar",
                adicionarCuenta: "Adición Cuenta",
                adicionarCuentaNueva: "Adición Cuenta Nueva",
                noData: "No hay cuentas de origen registradas",
                loading: "Cargando...",
                volver: "Volver"
            }

            return {
                ...defaultLabels,
                ...this.tableLabels
            }
        },

        tableFields() {
            return [
                {
                    key: 'cuenta',
                    label: this.internalLabels.cuenta,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-left align-middle',
                    minWidth: '120px'
                },
                {
                    key: 'descripcion',
                    label: this.internalLabels.descripcion,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-left align-middle',
                    minWidth: '200px'
                },
                {
                    key: 'valor',
                    label: this.internalLabels.valor,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-right align-middle',
                    minWidth: '120px'
                },
                {
                    key: 'funcional',
                    label: this.internalLabels.funcional,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-right align-middle',
                    minWidth: '100px'
                },
                {
                    key: 'alterna',
                    label: this.internalLabels.alterna,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-right align-middle',
                    minWidth: '100px'
                },
                {
                    key: 'accionEditar',
                    label: this.internalLabels.editar,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false,
                    minWidth: '60px'
                },
                {
                    key: 'accionEliminar',
                    label: this.internalLabels.eliminar,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false,
                    minWidth: '60px'
                }
            ]
        }
    },

    methods: {
        goBack() {
            this.$emit('back');
        },

        formatCurrency(value) {
            if (
                value === 0 ||
                value === "0" ||
                value === null ||
                value === "" ||
                value === undefined
            ) {
                return "";
            }

            return new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        },

        async editAjuste(id) {
            if (this.readOnly) {
                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'La vigencia seleccionada está deshabilitada para edición.'
                        : 'The selected validity is disabled for editing.'
                );

                return;
            }

            this.ajustes = this.ajustOrigen.find(
                (origen) => origen.id == id
            );

            this.showEdiAjustOrig = true
        },

        async borraCuentasOrigen(id) {
            if (this.readOnly) {
                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'La vigencia seleccionada está deshabilitada para eliminar.'
                        : 'The selected validity is disabled for deleting.'
                );

                return;
            }

            if (
                !confirm(
                    this.$store.state._language == "es-ES"
                        ? '¿Está seguro de que desea eliminar esta cuenta de origen?'
                        : 'Are you sure you want to delete this origin account?'
                )
            ) {
                return;
            }

            this.isLoading = true;

            try {
                const val = {
                    id: this.ajustOrigen[0]?.secuencia,
                    vigencia: this.vigencia,
                    idCompany: this.id_compania,
                    codReporte: this.codReporte,
                }

                await AjustesConsol.borraCuentasOrigen(id);

                this.$toast.success(
                    this.$store.state._language == "es-ES"
                        ? 'Cuenta eliminada correctamente'
                        : 'Account deleted successfully'
                );

                await this.recargaTabla(val);
            } catch (error) {
                console.error(
                    'Error al eliminar la cuenta:',
                    error
                );

                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'Error al eliminar la cuenta'
                        : 'Error deleting account'
                );
            } finally {
                this.isLoading = false;
            }
        },

        async adicionaCuentasOrigen() {
            if (this.readOnly) {
                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'La vigencia seleccionada está deshabilitada para crear o editar.'
                        : 'The selected validity is disabled for creating or editing.'
                );

                return;
            }

            try {
                const result =
                    await PlanCuentas.getCuentasPorCompaVigRepor(
                        this.id_compania,
                        this.vigencia,
                        this.codReporte
                    );

                this.listCuentas =
                    result.data.planCuentas || []

                this.listCuentasSelMul =
                    result.data.planCuentas || []

                this.ajustes = [
                    {
                        id_compania:
                            this.ajustOrigen[0]?.id_compania ||
                            this.id_compania
                    }
                ]

                this.showEdiAjustOrig = true
            } catch (err) {
                console.error(err)

                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'Error al cargar las cuentas'
                        : 'Error loading accounts'
                );
            }
        },

        adicionaCuentaNueva() {
            if (this.readOnly) {
                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'La vigencia seleccionada está deshabilitada para crear.'
                        : 'The selected validity is disabled for creating.'
                );

                return;
            }

            this.showModalCuentaNueva = true
        },

        cierraModal(val) {
            this.showEdiAjustOrig = val
        },

        cierraModalCuentaNueva(val) {
            this.showModalCuentaNueva = val
        },

        async cuentaCreada() {
            this.showModalCuentaNueva = false
            this.isLoading = true;

            try {
                const val = {
                    id: this.ajustOrigen[0]?.secuencia,
                    vigencia: this.vigencia,
                    idCompany: this.id_compania,
                }

                await this.recargaTabla(val)

                this.$toast.success(
                    this.$store.state._language == "es-ES"
                        ? 'Cuenta creada correctamente'
                        : 'Account created successfully'
                );
            } catch (error) {
                console.error(
                    'Error al crear la cuenta:',
                    error
                );

                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'Error al crear la cuenta'
                        : 'Error creating account'
                );
            } finally {
                this.isLoading = false;
            }
        },

        async getReportePrincipal() {
            try {
                const response =
                    await Ajustes.getReportePrincipal();

                return response.data?.codigo;
            } catch (error) {
                console.error(
                    'Error al obtener reporte principal:',
                    error
                );

                return null;
            }
        },

        async recargaTabla(val) {
            this.showEdiAjustOrig = false
            this.isLoading = true;

            try {
                const codReportePrincipal =
                    await this.getReportePrincipal();

                const ajusteOrig =
                    await AjustesConsol.consulAjusteOrigen(
                        val.id,
                        val.vigencia,
                        val.idCompany,
                        codReportePrincipal || this.codReporte
                    )

                this.$emit(
                    'update:ajustOrigen',
                    ajusteOrig.data.ajustOrig || []
                )
            } catch (error) {
                console.error(
                    'Error al recargar la tabla:',
                    error
                );

                this.$toast.error(
                    this.$store.state._language == "es-ES"
                        ? 'Error al recargar los datos'
                        : 'Error reloading data'
                );
            } finally {
                this.isLoading = false;
            }
        }
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.ajustes-detail-container {
    padding: 0;
    width: 100%;
}

.ajustes-detail-table-container {
    width: 100%;
}

.ajustes-detail-table {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

/* =========================================================
   TABLA INTERNA
   ========================================================= */

.ajustes-detail-table .tblEstandar.tblCorporate {
    min-width: 100% !important;
    width: max-content !important;
    table-layout: fixed !important;
}

.ajustes-detail-table .tblEstandar.tblCorporate td {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 0 !important;
}

.ajustes-detail-table .tblEstandar.tblCorporate thead th {
    white-space: nowrap !important;
}

/* =========================================================
   TOOLBAR PERSONALIZADO
   ========================================================= */

.detail-toolbar-left {
    flex: 1;
    min-width: 0;
}

.detail-toolbar-left h5 {
    font-size: 0.95rem;
    color: #2c3e50;
}

.detail-toolbar-left .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.6rem;
}

.detail-toolbar-right {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 4px;
}

/* =========================================================
   BOTONES DE ACCIÓN EN TABLA
   ========================================================= */

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* =========================================================
   BOTONES DE ADICIÓN
   ========================================================= */

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
}

.table-toolbar-add-btn-origin {
    background-color: #17a2b8;
    color: white;
    border: 1px solid #17a2b8;
}

.table-toolbar-add-btn-origin:hover:not(:disabled) {
    background-color: #138496;
    border-color: #117a8b;
}

.table-toolbar-add-btn-origin:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-toolbar-add-btn-origin-new {
    background-color: #007bff;
    color: white;
    border: 1px solid #007bff;
}

.table-toolbar-add-btn-origin-new:hover:not(:disabled) {
    background-color: #0069d9;
    border-color: #0062cc;
}

.table-toolbar-add-btn-origin-new:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* =========================================================
   MENSAJE SIN DATOS
   ========================================================= */

.admin-table-block {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.admin-table-block h5 {
    color: #6c757d;
    text-align: center;
    margin: 0;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */

@media (max-width: 768px) {
    .ajustes-detail-container {
        min-height: 450px;
    }

    .ajustes-detail-table-container {
        min-height: 450px;
    }

    .ajustes-detail-table {
        min-height: 450px;
        height: 450px;
    }

    .detail-toolbar-left h5 {
        font-size: 0.8rem;
    }

    .table-toolbar-add-btn-origin,
    .table-toolbar-add-btn-origin-new {
        font-size: 11px;
        padding: 0 10px;
        height: 28px;
        min-height: 28px;
    }
}

@media (max-width: 576px) {
    .ajustes-detail-container {
        min-height: 400px;
    }

    .ajustes-detail-table-container {
        min-height: 400px;
    }

    .ajustes-detail-table {
        min-height: 400px;
        height: 400px;
    }

    .detail-toolbar-left h5 {
        font-size: 0.75rem;
    }

    .detail-toolbar-right {
        flex-wrap: wrap;
        gap: 4px;
    }

    .table-toolbar-add-btn-origin,
    .table-toolbar-add-btn-origin-new {
        font-size: 10px;
        padding: 0 8px;
        height: 26px;
        min-height: 26px;
    }
}
</style>